/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, FileText, AlertCircle, CheckCircle2, Banknote, Trash2, Upload, Download, Sparkles, Link2 } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

const TYPES = [
    { value: "gst", label: "GST Return" },
    { value: "income_tax", label: "Income Tax Return" },
    { value: "provisional", label: "Provisional Tax" },
    { value: "paye", label: "PAYE Filing" },
    { value: "fbt", label: "FBT Return" },
];

const STATUSES = [
    { value: "drafting", label: "Drafting", tone: "bg-zinc-100 text-zinc-700 border-zinc-200" },
    { value: "awaiting_info", label: "Awaiting info", tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { value: "ready_for_review", label: "Ready for review", tone: "bg-sky-50 text-sky-800 border-sky-200" },
    { value: "filed", label: "Filed", tone: "bg-violet-50 text-violet-800 border-violet-200" },
    { value: "paid", label: "Paid", tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    { value: "refunded", label: "Refunded", tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
];

const labelOf = (arr, v) => arr.find((x) => x.value === v)?.label || v;
const toneOf = (v) => STATUSES.find((x) => x.value === v)?.tone || "bg-zinc-100 text-zinc-700 border-zinc-200";

function daysUntil(date) {
    if (!date) return null;
    const d = new Date(date + "T00:00:00").getTime();
    return Math.round((d - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function TaxReturns() {
    const [returns, setReturns] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterType, setFilterType] = useState("all");
    const [filterClient, setFilterClient] = useState("all");
    const [openNew, setOpenNew] = useState(false);
    const [active, setActive] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const [r, c] = await Promise.all([
                api.get("/tax-returns/"),
                api.get("/clients/"),
            ]);
            setReturns(r.data);
            setClients(c.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

    const filtered = useMemo(() => returns.filter((r) =>
        (filterStatus === "all" || r.status === filterStatus) &&
        (filterType === "all" || r.tax_type === filterType) &&
        (filterClient === "all" || r.client_id === filterClient)
    ), [returns, filterStatus, filterType, filterClient]);

    const counts = useMemo(() => {
        const overdue = returns.filter((r) => r.status !== "paid" && r.status !== "refunded" && daysUntil(r.due_date) < 0).length;
        const dueSoon = returns.filter((r) => r.status !== "paid" && r.status !== "refunded" && daysUntil(r.due_date) >= 0 && daysUntil(r.due_date) <= 14).length;
        const open = returns.filter((r) => !["paid", "refunded"].includes(r.status)).length;
        return { overdue, dueSoon, open };
    }, [returns]);

    return (
        <div data-testid="tax-returns-page">
            <PageHeader
                eyebrow="Compliance"
                title="Tax returns"
                description="Every GST, Income Tax, PAYE, Provisional and FBT return — when it's due, where it is, and whether the client has paid."
                testId="tax-returns-header"
            >
                <Dialog open={openNew} onOpenChange={setOpenNew}>
                    <DialogTrigger asChild>
                        <Button className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="tr-new-btn">
                            <Plus className="h-4 w-4" /> New return
                        </Button>
                    </DialogTrigger>
                    <NewReturnDialog clients={clients} onCreated={() => { setOpenNew(false); load(); }} />
                </Dialog>
            </PageHeader>

            <div className="grid grid-cols-3 gap-3 mb-4" data-testid="tr-stats">
                <Stat label="Open" value={counts.open} />
                <Stat label="Due in ≤14 days" value={counts.dueSoon} tone="text-amber-700" />
                <Stat label="Overdue" value={counts.overdue} tone="text-rose-700" />
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
                <FilterPill label="All" active={filterStatus === "all"} onClick={() => setFilterStatus("all")} testId="tr-filter-all" />
                {STATUSES.map((s) => (
                    <FilterPill key={s.value} label={s.label} active={filterStatus === s.value} onClick={() => setFilterStatus(s.value)} testId={`tr-filter-${s.value}`} />
                ))}
                <div className="ml-auto flex gap-2">
                    <Select value={filterType} onValueChange={setFilterType}>
                        <SelectTrigger className="w-44 h-8 text-xs" data-testid="tr-filter-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All tax types</SelectItem>
                            {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={filterClient} onValueChange={setFilterClient}>
                        <SelectTrigger className="w-48 h-8 text-xs" data-testid="tr-filter-client"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All clients</SelectItem>
                            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-[#5E636E]"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : filtered.length === 0 ? (
                <EmptyState icon={FileText} title="No tax returns yet" description="Click 'New return' to add the first GST or income tax return." testId="tr-empty" />
            ) : (
                <div className="overflow-hidden rounded-lg border border-[#E4E4E7] bg-white" data-testid="tr-table">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F5F4EF] text-[10px] uppercase tracking-[0.12em] text-[#5E636E]">
                            <tr>
                                <th className="px-3 py-2 text-left">Client</th>
                                <th className="px-3 py-2 text-left">Type</th>
                                <th className="px-3 py-2 text-left">Period end</th>
                                <th className="px-3 py-2 text-left">Due</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                                <th className="px-3 py-2 text-left">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => {
                                const dleft = daysUntil(r.due_date);
                                const overdue = dleft < 0 && !["paid", "refunded"].includes(r.status);
                                const dueSoon = !overdue && dleft <= 14 && !["paid", "refunded"].includes(r.status);
                                return (
                                    <tr
                                        key={r.id}
                                        onClick={() => setActive(r)}
                                        className="border-t border-[#E4E4E7] hover:bg-[#FAFAFA] cursor-pointer"
                                        data-testid={`tr-row-${r.id}`}
                                    >
                                        <td className="px-3 py-2 font-medium">
                                            <div className="flex items-center gap-1.5">
                                                {clientName(r.client_id)}
                                                {r.external_source === "practice_manager" && (
                                                    <span className="inline-flex items-center gap-0.5 rounded-full bg-[#F5F4EF] px-1.5 py-0.5 text-[9px] font-medium text-[#0E3B2E] border border-[#0E3B2E]/20" title="Synced from Practice Manager" data-testid={`tr-pm-badge-${r.id}`}>
                                                    <Link2 className="h-2.5 w-2.5" /> PM
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">{labelOf(TYPES, r.tax_type)}</td>
                                        <td className="px-3 py-2 text-[#5E636E]">{r.period_end}</td>
                                        <td className={`px-3 py-2 ${overdue ? "text-rose-700 font-medium" : dueSoon ? "text-amber-700 font-medium" : "text-[#5E636E]"}`}>
                                            {r.due_date}{overdue && <span className="ml-1 text-[10px]">· {Math.abs(dleft)}d late</span>}{dueSoon && <span className="ml-1 text-[10px]">· in {dleft}d</span>}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">{typeof r.amount === "number" ? `$${r.amount.toLocaleString()}` : "—"}</td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${toneOf(r.status)}`}>{labelOf(STATUSES, r.status)}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {active && (
                <ReturnDetailDialog
                    row={active}
                    clientName={clientName(active.client_id)}
                    onClose={() => setActive(null)}
                    onChanged={() => { setActive(null); load(); }}
                />
            )}
        </div>
    );
}

function Stat({ label, value, tone = "" }) {
    return (
        <div className="rounded-lg border border-[#E4E4E7] bg-white p-3">
            <div className="co-label">{label}</div>
            <div className={`mt-1 font-display text-2xl ${tone}`}>{value}</div>
        </div>
    );
}

function FilterPill({ label, active, onClick, testId }) {
    return (
        <button type="button" onClick={onClick} className={`rounded-full border px-3 py-1 text-xs transition-colors ${active ? "bg-[#0E3B2E] text-white border-[#0E3B2E]" : "bg-white text-[#5E636E] border-[#E4E4E7] hover:bg-zinc-50"}`} data-testid={testId}>{label}</button>
    );
}

function NewReturnDialog({ clients, onCreated }) {
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        client_id: clients[0]?.id || "",
        tax_type: "gst",
        period_start: "",
        period_end: today,
        due_date: today,
        amount: "",
        ird_reference: "",
        notes: "",
    });
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e?.preventDefault();
        if (!form.client_id) { toast.error("Pick a client"); return; }
        setBusy(true);
        try {
            await api.post("/tax-returns/", {
                ...form,
                amount: form.amount === "" ? null : Number(form.amount),
                ird_reference: form.ird_reference || null,
                notes: form.notes || null,
            });
            toast.success("Tax return created");
            onCreated?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    function s(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    return (
        <DialogContent className="max-w-lg" data-testid="tr-new-dialog">
            <DialogHeader>
                <DialogTitle>New tax return</DialogTitle>
                <DialogDescription>Add a return so the client sees it in their portal. Status starts as Drafting.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="tr-new-form">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Client</Label>
                        <Select value={form.client_id} onValueChange={(v) => s("client_id", v)}>
                            <SelectTrigger className="mt-1.5" data-testid="tr-client"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Tax type</Label>
                        <Select value={form.tax_type} onValueChange={(v) => s("tax_type", v)}>
                            <SelectTrigger className="mt-1.5" data-testid="tr-type"><SelectValue /></SelectTrigger>
                            <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Period start</Label>
                        <Input type="date" value={form.period_start} onChange={(e) => s("period_start", e.target.value)} className="mt-1.5" data-testid="tr-period-start" />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Period end</Label>
                        <Input type="date" value={form.period_end} onChange={(e) => s("period_end", e.target.value)} className="mt-1.5" data-testid="tr-period-end" required />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Due date</Label>
                        <Input type="date" value={form.due_date} onChange={(e) => s("due_date", e.target.value)} className="mt-1.5" data-testid="tr-due" required />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Amount (NZD)</Label>
                        <Input type="number" step="0.01" value={form.amount} onChange={(e) => s("amount", e.target.value)} placeholder="0.00" className="mt-1.5" data-testid="tr-amount" />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">IRD reference</Label>
                        <Input value={form.ird_reference} onChange={(e) => s("ird_reference", e.target.value)} className="mt-1.5" data-testid="tr-ird-ref" />
                    </div>
                </div>
                <div>
                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Notes (visible to client)</Label>
                    <Textarea rows={2} value={form.notes} onChange={(e) => s("notes", e.target.value)} className="mt-1.5" data-testid="tr-notes" />
                </div>
                <DialogFooter>
                    <Button type="submit" disabled={busy || !form.client_id} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="tr-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create return"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}

function ReturnDetailDialog({ row, clientName, onClose, onChanged }) {
    const [form, setForm] = useState({
        tax_type: row.tax_type,
        period_start: row.period_start || "",
        period_end: row.period_end,
        due_date: row.due_date,
        amount: row.amount ?? "",
        ird_reference: row.ird_reference || "",
        status: row.status,
        notes: row.notes || "",
    });
    const [busy, setBusy] = useState(false);

    async function save() {
        setBusy(true);
        try {
            await api.patch(`/tax-returns/${row.id}`, {
                ...form,
                amount: form.amount === "" ? null : Number(form.amount),
                ird_reference: form.ird_reference || null,
                notes: form.notes || null,
            });
            toast.success(form.status === row.status ? "Saved" : `Moved to ${labelOf(STATUSES, form.status)}`);
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function del() {
        if (!window.confirm("Delete this tax return? This can't be undone.")) return;
        setBusy(true);
        try { await api.delete(`/tax-returns/${row.id}`); toast.success("Deleted"); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }
    function s(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-lg" data-testid="tr-detail-dialog">
                <DialogHeader>
                    <DialogTitle>{clientName} · {labelOf(TYPES, row.tax_type)}</DialogTitle>
                    <DialogDescription>Period ending {row.period_end} · created {new Date(row.created_at).toLocaleDateString()}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Status</Label>
                        <Select value={form.status} onValueChange={(v) => s("status", v)}>
                            <SelectTrigger className="mt-1.5" data-testid="tr-detail-status"><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUSES.map((st) => <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <p className="mt-1.5 text-[11px] text-[#5E636E]">Clients see live status updates in their portal. No emails are sent automatically.</p>
                    </div>
                    <div className="col-span-2">
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">IRD return PDF</Label>
                        <PdfUploadBlock row={row} onChanged={onChanged} />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Period start</Label>
                        <Input type="date" value={form.period_start} onChange={(e) => s("period_start", e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Period end</Label>
                        <Input type="date" value={form.period_end} onChange={(e) => s("period_end", e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Due date</Label>
                        <Input type="date" value={form.due_date} onChange={(e) => s("due_date", e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Amount (NZD)</Label>
                        <Input type="number" step="0.01" value={form.amount} onChange={(e) => s("amount", e.target.value)} className="mt-1.5" />
                    </div>
                    <div className="col-span-2">
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">IRD reference</Label>
                        <Input value={form.ird_reference} onChange={(e) => s("ird_reference", e.target.value)} className="mt-1.5" />
                    </div>
                    <div className="col-span-2">
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Notes (visible to client)</Label>
                        <Textarea rows={3} value={form.notes} onChange={(e) => s("notes", e.target.value)} className="mt-1.5" />
                    </div>
                    {row.client_acknowledged_at && (
                        <div className="col-span-2 rounded-md bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-900 inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Client acknowledged {new Date(row.client_acknowledged_at).toLocaleString()}</div>
                    )}
                    {row.paid_at && (
                        <div className="col-span-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 inline-flex items-center gap-1.5"><Banknote className="h-3.5 w-3.5" /> Paid {new Date(row.paid_at).toLocaleDateString()}{row.paid_bank_reference ? ` · ref ${row.paid_bank_reference}` : ""}</div>
                    )}
                </div>
                <DialogFooter className="justify-between">
                    <Button variant="outline" onClick={del} disabled={busy} className="text-rose-700 hover:bg-rose-50" data-testid="tr-detail-delete"><Trash2 className="h-4 w-4" /> Delete</Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose}>Cancel</Button>
                        <Button onClick={save} disabled={busy} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="tr-detail-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function PdfUploadBlock({ row, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [last, setLast] = useState(null);
    const hasFile = !!row.pdf_file_name;

    async function upload(file) {
        if (!file) return;
        if (file.size > 15 * 1024 * 1024) { toast.error("PDF too large (max 15MB)"); return; }
        const fd = new FormData();
        fd.append("file", file);
        setBusy(true);
        try {
            const { data } = await api.post(`/tax-returns/${row.id}/pdf`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setLast(data?.extracted || {});
            if (data?.extracted?.amount != null) {
                toast.success(`PDF uploaded — amount $${Number(data.extracted.amount).toLocaleString()}`);
            } else {
                toast.success("PDF uploaded — couldn't auto-detect amount, please set it manually.");
            }
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function download() {
        try {
            const res = await api.get(`/tax-returns/${row.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = row.pdf_file_name || "ird-return.pdf";
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div className="mt-1.5 rounded-md border border-dashed border-[#E4E4E7] p-3">
            {hasFile && (
                <div className="flex items-center justify-between gap-2 mb-2 rounded bg-[#FAFAFA] px-2 py-1.5">
                    <div className="text-xs truncate"><FileText className="inline h-3.5 w-3.5 mr-1 text-[#5E636E]" /> {row.pdf_file_name}</div>
                    <Button size="sm" variant="outline" type="button" onClick={download} className="h-7 text-xs" data-testid="tr-pdf-download"><Download className="h-3.5 w-3.5" /> Download</Button>
                </div>
            )}
            <label className="block">
                <input
                    type="file"
                    accept="application/pdf"
                    disabled={busy}
                    onChange={(e) => upload(e.target.files?.[0])}
                    className="hidden"
                    data-testid="tr-pdf-input"
                />
                <span className={`inline-flex items-center gap-1.5 cursor-pointer rounded-md border border-[#0E3B2E] bg-white px-3 py-1.5 text-xs font-medium text-[#0E3B2E] hover:bg-[#F5F4EF] ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="tr-pdf-upload">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    {hasFile ? "Replace PDF" : "Upload IRD PDF"}
                </span>
            </label>
            {last && last.source && last.source !== "empty" && (
                <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-violet-50 border border-violet-200 px-2 py-0.5 text-[10px] text-violet-800">
                    <Sparkles className="h-3 w-3" /> Amount extracted by {last.source === "claude" ? "Claude" : "pattern match"}
                </div>
            )}
            {row.amount_extracted && !last && (
                <div className="mt-2 text-[10px] text-[#5E636E]">Amount was auto-extracted from the PDF — staff edits override it.</div>
            )}
            <p className="mt-2 text-[10px] text-[#5E636E]">Upload the IRD return PDF — Claude reads it and fills in the amount payable. The client can download the PDF from their portal.</p>
        </div>
    );
}
